import { fetchData } from '../utils/netUtils';
import {
  resolveCurrentEnvironment
} from '../utils/configUtils';
import { decodeToken } from '../utils/jwtUtils';

const unityAuthService = {
  /**
  * Retrieves client's hub configurations
  *
  * @param {*} tokenData Authorization token
  * @returns {Object} config list
  */
  getClientConfig: async (tokenData) => {
    const currentENV = resolveCurrentEnvironment();
    const URL = `${currentENV.TBX_UNITY_API}/configs`;
    const options = {
      credentials: 'include',
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `${tokenData['auth_type']} ${tokenData['access_token']}`,
        'Content-Type': 'application/json;charset=utf-8'
      },
    };

    return fetchData(URL, options);
  },

  /**
   * Retrieves/renews client public JWT token
   *
   * @param {String} country (optional) Specifies user country
   * @param {String} currentProfile (optional) User current profile
   * @param {String} device (optional) valid user cloudpass device
   * @param {String} language (optional) Specifies user language
   * @param {String} oneTimeToken (optional) expiration token
   * @param {String} version (optional) valid device_version
   * @returns {Object} JWT Token data
   */
   getPublicAuth: async (
    country,
    currentProfile,
    device,
    language,
    oneTimeToken,
    version,
    SO
  ) => {
    const currentENV = resolveCurrentEnvironment();
    const URL = `${currentENV.TBX_UNITY_API}/auth/public`;

    const mapAuth = new Map();
    mapAuth.set("country", country);
    mapAuth.set("currentProfile", currentProfile);
    mapAuth.set("device", device);
    mapAuth.set("language", language);

    if (oneTimeToken) {
        mapAuth.set("temporalDevice", oneTimeToken);
    }
    if (version) {
        mapAuth.set("version", version);
    }
    let auth = Object.fromEntries(mapAuth)

    let body = {
      auth: {
        sub: SO && SO === 'ios' && currentENV?.TBX_CLIENT_KEY_IOS ? currentENV?.TBX_CLIENT_KEY_IOS : currentENV.TBX_CLIENT_KEY,
        ...auth
      }
    };

    if (currentENV.TBX_CLIENTS_LIST) {
      delete(body.auth.sub);
      body.clients = currentENV.TBX_CLIENTS_LIST
    }

    const options = {
      credentials: 'omit',
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json;charset=utf-8'
      },
      body: JSON.stringify(body)
    };

    return fetchData(URL, options);
  },

  /**
  * Retrieves/true if logout was succesfull
  *
  * @param {*} tokenData Authorization token
  * @returns {Object} {ok:true}
  */
  logout: async (
    tokenData,
    data
  ) => {

    let theData = data || { }
    let { version } = theData

    const currentENV = resolveCurrentEnvironment();
    const URL = `${currentENV.TBX_UNITY_API}/auth/logout`;
    const requestBody = JSON.stringify({ version: version ?? "" });
    
    const options = {
      credentials: 'omit',
      method: 'POST',
      body: requestBody,
      headers: {
        Accept: 'application/json',
        Authorization: `${tokenData['auth_type']} ${tokenData['access_token']}`,
        'Content-Type': 'application/json;charset=utf-8'
      },
    };

    return fetchData(URL, options);
  },


  /**
  * Retrieves Network configurations
  *
  * @param {*} tokenData Authorization token
  * @returns {Object} Networks list
  */
   getNetworks: async (tokenData) => {
    const currentENV = resolveCurrentEnvironment();
    const URL = `${currentENV.TBX_UNITY_API}/networks`;
    const options = {
      credentials: 'include',
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `${tokenData['auth_type']} ${tokenData['access_token']}`,
        'Content-Type': 'application/json;charset=utf-8'
      },
    };

    return fetchData(URL, options);
  },


  /**
  * Retrieves Channel configurations
  *
  * @param {*} tokenData Authorization token
  * @returns {Object} Channels list
  */
   getChannels: async (tokenData) => {
    const currentENV = resolveCurrentEnvironment();
    const URL = `${currentENV.TBX_UNITY_API}/channels`;
    const options = {
      credentials: 'include',
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `${tokenData['auth_type']} ${tokenData['access_token']}`,
        'Content-Type': 'application/json;charset=utf-8'
      },
    };

    return fetchData(URL, options);
  },

  /**
  * Activation code's socketURL
  *
  * @param {*} tokenData Authorization token
  * @returns {Boolean}
  */
  getActivationCodeSocketURL: async (tokenData) => {
    const currentENV = resolveCurrentEnvironment();
    const URL = `${currentENV.TBX_UNITY_API}/auth/autologin/code/socket`;
    const options = {
      credentials: 'include',
      method: 'GET',
      headers: {
        Accept: 'application/json',
        Authorization: `${tokenData['auth_type']} ${tokenData['access_token']}`,
        'Content-Type': 'application/json;charset=utf-8'
      },
    };

    return fetchData(URL, options);
  },

  /**
  * Validate device' code
  *
  * @param {*} tokenData Authorization token
  * @param {*} payload Code for validation
  * @returns {Boolean}
  */
   validateActivateCode: async (tokenData, payload) => {
    const currentENV = resolveCurrentEnvironment();
    const URL = `${currentENV.TBX_UNITY_API}/auth/autologin/code`;
    const body = {
      code: payload.code
    };
    const options = {
      credentials: 'include',
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `${tokenData['auth_type']} ${tokenData['access_token']}`,
        'Content-Type': 'application/json;charset=utf-8'
      },
      body: JSON.stringify(body)
    };

    return fetchData(URL, options);
  }

};

export default unityAuthService;
